import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Stamp, ShieldCheck, ShieldAlert, RefreshCw, ExternalLink, Loader2 } from "lucide-react";

const GOLD = "#C9A84C";

export function AdminSealDesk({ api }) {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setRows(null);
    try {
      const quick = await api.get("/admin/seal-desk/templates", { params: { scan: false } });
      setRows(quick.data.templates);
      const r = await api.get("/admin/seal-desk/templates");
      setRows(r.data.templates);
    } catch { toast.error("Could not load the template gallery"); setRows([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const open = async (t) => {
    setActive(t); setBusy(true);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    try {
      const r = await api.get(`/admin/seal-desk/preview/${t.key}`, { responseType: "blob" });
      setBlobUrl(URL.createObjectURL(r.data));
    } catch { toast.error(`Could not render ${t.title}`); setBlobUrl(null); }
    finally { setBusy(false); }
  };
  const families = rows ? [...new Set(rows.map((r) => r.family))] : [];
  const okCount = rows ? rows.filter((r) => r.seal_ok && r.names_found?.length === 0).length : 0;

  return (
    <div data-testid="seal-desk" className="space-y-6">
      <div className="border border-[#C9A84C]/30 rounded-sm p-6 bg-[#0f0d0a]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.4em]" style={{ color: GOLD }}>· Compliance · Seal Preview Desk ·</div>
            <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-3 flex items-center gap-3"><Stamp size={22} style={{ color: GOLD }} /> Every template, under the Common Seal</h2>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">Each document below is rendered live with sample data and scanned: the block <b className="text-[#F5F5F0]">VINCI INC. · COMMON SEAL — AUTHORIZED SIGNATORY · VINCI TRADE DESK</b> must be present and no individual's name may appear.</p>
          </div>
          <div className="text-right">
            <div data-testid="seal-desk-score" className="font-serif text-3xl" style={{ color: GOLD }}>{rows ? `${okCount}/${rows.length}` : "…"}</div>
            <div className="font-grotesk text-[8px] uppercase tracking-[0.3em] text-[#8A8A82]">templates sealed</div>
            <button data-testid="seal-desk-rescan" onClick={load} className="mt-3 inline-flex items-center gap-2 border border-white/20 text-[#C9C9C2] px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.24em] hover:border-[#C9A84C]"><RefreshCw size={11} /> Re-scan</button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="space-y-5">
          {!rows && <div className="h-40 animate-pulse bg-white/5" />}
          {families.map((f) => (
            <div key={f}>
              <div className="font-grotesk text-[8px] uppercase tracking-[0.34em] text-[#8A8A82] mb-2">{f}</div>
              <ul className="space-y-1.5">
                {rows.filter((r) => r.family === f).map((t) => {
                  const clean = t.seal_ok && (t.names_found || []).length === 0 && t.status !== "error";
                  return (
                    <li key={t.key}>
                      <button data-testid={`seal-template-${t.key}`} onClick={() => open(t)}
                              className={`w-full text-left flex items-center justify-between gap-3 border px-3 py-2.5 transition-colors ${active?.key === t.key ? "border-[#C9A84C] bg-[#C9A84C]/10" : "border-white/10 bg-black/40 hover:border-[#C9A84C]/50"}`}>
                        <div className="min-w-0">
                          <div className="font-serif text-sm text-[#F5F5F0] truncate">{t.title}</div>
                          <div className="font-mono text-[9px] text-[#8A8A82]">{t.kind.toUpperCase()}{t.pages ? ` · ${t.pages} page${t.pages > 1 ? "s" : ""}` : ""}{t.status === "error" ? ` · ${t.error}` : ""}</div>
                        </div>
                        {clean ? <ShieldCheck size={16} className="shrink-0" style={{ color: GOLD }} data-testid={`seal-ok-${t.key}`} />
                               : <ShieldAlert size={16} className="shrink-0 text-[#C0392B]" title={(t.names_found || []).join(", ") || "Seal block missing"} data-testid={`seal-bad-${t.key}`} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        <div className="border border-white/10 bg-black/60 rounded-sm min-h-[560px] flex flex-col" data-testid="seal-preview-pane">
          <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-white/10">
            <div className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#8A8A82] truncate">{active ? active.title : "Select a template to preview"}</div>
            {blobUrl && <a href={blobUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.24em]" style={{ color: GOLD }}><ExternalLink size={11} /> Open</a>}
          </div>
          <div className="flex-1 relative">
            {busy && <div className="absolute inset-0 grid place-items-center"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>}
            {blobUrl && !busy && <iframe title="preview" src={blobUrl} className="w-full h-full min-h-[540px] bg-white" data-testid="seal-preview-frame" />}
            {!active && !busy && <div className="absolute inset-0 grid place-items-center font-serif text-[#4A4A44]">The seal awaits.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
